import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ArrowUpRight } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { Reveal } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const Stars = ({ n = 0, size = 14 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n} stelle su 5`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} className={i <= Math.round(n) ? "fill-[#C9A54A] text-[#C9A54A]" : "text-[#1B3B28]/20"} />)}
  </span>
);

const ReviewCard = ({ r, i }) => (
  <Reveal delay={(i % 3) * 0.08}>
    <figure className="h-full flex flex-col rounded-3xl bg-[#FCFBF7] border border-[#1B3B28]/10 p-7" data-testid={`review-card-${i}`}>
      <Stars n={r.rating} />
      <blockquote className="mt-4 text-[#1E2922] leading-relaxed line-clamp-[8] flex-1">{r.text}</blockquote>
      <figcaption className="mt-6 flex items-center gap-3">
        {r.photo ? <img src={r.photo} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full" loading="lazy" /> : <span className="grid place-items-center w-9 h-9 rounded-full bg-[#E6ECE4] text-[#1B3B28] text-sm">{r.author?.[0]}</span>}
        <span className="text-sm"><a href={r.author_uri || undefined} target="_blank" rel="noopener noreferrer" className="block text-[#1B3B28] font-medium">{r.author}</a><span className="text-[#526356]">{r.when}</span></span>
      </figcaption>
    </figure>
  </Reveal>
);

const LeaveReview = ({ url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" data-testid="reviews-leave-btn"
    className="group inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-6 py-3.5 text-sm hover:bg-[#3E6B48] transition-colors">
    Sei già nostro paziente? Lascia una recensione <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:rotate-45" />
  </a>
);

export const Reviews = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!API) return;
    axios.get(`${API}/api/reviews`).then(({ data: d }) => setData(d)).catch(() => {});
  }, []);
  const list = Array.isArray(data?.reviews) ? data.reviews.filter((r) => r.text) : [];
  if (!list.length) return null;
  return (
    <section id="recensioni" className="py-28 sm:py-40 bg-[#F4F3EC]" data-testid="reviews-section">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <SectionHead eyebrow="Dicono di noi" lines={["Le parole", "dei pazienti."]} />
          {data.rating && (
            <Reveal className="lg:text-right" delay={0.1}>
              <p className="f-serif text-7xl text-[#1B3B28] leading-none" data-testid="reviews-rating">{data.rating.toFixed(1).replace(".", ",")}</p>
              <div className="mt-3 flex lg:justify-end items-center gap-3"><Stars n={data.rating} size={18} /><span className="text-sm text-[#526356]">{data.count} recensioni su Google</span></div>
            </Reveal>
          )}
        </div>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">{list.slice(0, 6).map((r, i) => <ReviewCard key={i} r={r} i={i} />)}</div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <LeaveReview url={data.review_url} />
          {data.maps_uri && <a href={data.maps_uri} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1B3B28] underline underline-offset-4" data-testid="reviews-maps-link">Leggi tutte su Google</a>}
        </div>
        <p className="mt-6 text-xs text-[#526356]">Recensioni fornite da Google.</p>
      </div>
    </section>
  );
};
